import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getChildOffers, getParentOffers, clearHeroOfferCache } from '../services/heroOfferService'
import { getProfessionals } from '../services/professionalService'
import { useCurrency } from '../contexts/CurrencyContext'
import { formatPrice } from '../utils/currencyUtils'
import OptimizedImage from '../utils/OptimizedImage'

import { useSalon } from '../contexts/SalonContext'
import { calculatePackagePricing, formatOfferValidityRange } from '../utils/packageOfferUtils'

// Helper function to slugify campaign titles
export function slugify(text) {
  if (!text) return ''
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
}

const OffersPage = () => {
  const { campaignSlug } = useParams()
  const navigate = useNavigate()
  const { currency } = useCurrency()
  const { salonId, salonReady } = useSalon()

  const [parentOffer, setParentOffer] = useState(null)
  const [childOffers, setChildOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [campaignSlug])

  useEffect(() => {
    if (!campaignSlug || !salonId || !salonReady) return

    const loadData = async () => {
      try {
        setLoading(true)
        setError(null)
        clearHeroOfferCache()

        // 1. Fetch all parent offers for this salon to find the matching slug
        const { data: parentRows, error: parentError } = await getParentOffers(salonId)
        if (parentError) throw parentError

        const matchedParent = (parentRows || []).find(
          (o) => slugify(o.title) === campaignSlug
        )

        if (!matchedParent) {
          setError('Offer campaign not found.')
          setLoading(false)
          return
        }

        setParentOffer(matchedParent)

        // 2. Fetch child offers under the matched parent ID
        const result = await getChildOffers(matchedParent.id)
        if (result.error) throw result.error
        setChildOffers(result.data || [])

        // Warm the cache the package detail page reads so opening a package is instant.
        getProfessionals().catch(() => {})
      } catch (err) {
        console.error('Error loading offer details:', err)
        setError('Failed to load offers. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [campaignSlug, salonId, salonReady])

  const handleOpenPackage = (offer) => {
    navigate(`/offers/${campaignSlug}/package/${offer.id}`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 pt-20">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mb-2"></div>
          <p className="text-slate-600 font-medium">Loading exclusive deals...</p>
        </div>
      </div>
    )
  }

  if (error || !parentOffer) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 pt-20 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-slate-100">
          <div className="text-red-500 text-5xl mb-4">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Deals Not Found</h2>
          <p className="text-slate-600 mb-6">{error || 'This offer campaign could not be located.'}</p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-black hover:bg-slate-800 text-white font-medium py-3 rounded-xl transition duration-200"
          >
            Back to Homepage
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20 pt-16 sm:pt-20 font-sans">
      {/* Banner Header Section */}
      <div className="relative w-full overflow-hidden bg-slate-950 h-[260px] sm:h-[320px] flex items-center justify-center">
        {parentOffer.image_url ? (
          <OptimizedImage
            src={parentOffer.image_url}
            alt={parentOffer.title}
            className="absolute inset-0 w-full h-full object-cover opacity-65"
            loading="eager"
            sizes="100vw"
            width={1600}
            height={400}
          />
        ) : null}
        
        {/* HSL Gradient overlay for premium feel */}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-slate-950/20 to-transparent"></div>
        
        {/* Top-Left Back Button */}
        <button
          onClick={() => navigate('/')}
          className="absolute top-6 left-4 sm:left-8 z-20 inline-flex items-center gap-2 text-white/80 hover:text-white text-xs font-bold uppercase tracking-wider bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full backdrop-blur-md transition-all duration-300 border border-white/15 shadow-sm active:scale-95"
        >
          <i className="fas fa-arrow-left text-[10px]"></i> Back
        </button>
        
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto mt-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-3 tracking-wide drop-shadow-md uppercase">
            {parentOffer.title}
          </h1>
          {parentOffer.subtitle ? (
            <p className="text-slate-200 text-xs sm:text-sm md:text-base max-w-xl mx-auto font-medium drop-shadow-sm leading-relaxed tracking-wide opacity-90">
              {parentOffer.subtitle}
            </p>
          ) : null}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-6xl mx-auto px-4 mt-12 sm:mt-16">
        {childOffers.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-100 shadow-md p-8">
            <div className="text-slate-400 text-5xl mb-4">
              <i className="fas fa-tag"></i>
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-1">No Active Sub-Deals</h3>
            <p className="text-slate-500 max-w-sm mx-auto">There are currently no sub-deals active for this campaign. Please check back later!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {childOffers.map((offer) => {
              const pricing = calculatePackagePricing(offer.services || [], offer.discount_percent)
              const validity = formatOfferValidityRange(offer, parentOffer)
              return (
                <div 
                  key={offer.id} 
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenPackage(offer)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') handleOpenPackage(offer)
                  }}
                  className="group bg-white rounded-3xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] border border-slate-100/80 transition-all duration-300 flex flex-col justify-between transform hover:-translate-y-1 cursor-pointer"
                >
                  <div>
                    {/* Offer Image — rounded to match card */}
                    <div className="relative p-3 pb-0">
                      <div className="relative h-36 sm:h-40 overflow-hidden rounded-2xl bg-slate-50 border border-slate-100">
                        {offer.image_url ? (
                          <OptimizedImage
                            src={offer.image_url}
                            alt={offer.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                            sizes="(max-width: 768px) 100vw, 50vw"
                            width={800}
                            height={500}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-slate-400">
                            <i className="fas fa-image text-3xl"></i>
                          </div>
                        )}
                        
                        {offer.discount_percent > 0 ? (
                          <div className="absolute top-3 left-3 bg-purple-600 text-white font-extrabold text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full shadow-md">
                            {offer.discount_percent}% OFF
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {/* Offer Text Details */}
                    <div className="p-5 sm:p-6">
                      {offer.total_days > 1 ? (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-xs font-bold mb-3">
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></span>
                          ⚡ Multi-day Package ({offer.total_days} Days)
                        </div>
                      ) : null}
                      <h3 className="text-lg sm:text-xl font-bold text-slate-800 leading-snug mb-1.5 group-hover:text-purple-600 transition-colors">
                        {offer.title}
                      </h3>
                      {offer.subtitle ? (
                        <p className="text-slate-500 text-xs leading-relaxed mb-3 line-clamp-2">
                          {offer.subtitle}
                        </p>
                      ) : null}
                      {validity ? (
                        <div className="flex items-start gap-3 mb-4">
                          <span className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                            <i className="far fa-calendar-check text-sm" />
                          </span>
                          <div className="min-w-0">
                            <p className="font-semibold text-slate-900 text-sm">Deal validity</p>
                            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{validity}</p>
                          </div>
                        </div>
                      ) : null}

                      {/* Services Included */}
                      <div className="space-y-2 mt-2">
                        <h4 className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                          Services Included:
                        </h4>
                        <div className="flex flex-wrap gap-1.5">
                          {offer.services.map((svc) => (
                            <span 
                              key={svc.id} 
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 px-2 py-1 rounded-lg border border-slate-200/50"
                            >
                              <i className="fas fa-check text-purple-500 text-[8px]"></i>
                              {svc.name} {offer.total_days > 1 && svc.day_number ? `(Day ${svc.day_number})` : ''}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pricing and Booking Actions */}
                  <div className="p-5 sm:p-6 pt-0 border-t border-slate-100 bg-slate-50/50">
                    <div className="flex items-center justify-between gap-4 mt-3">
                      {/* Price Section */}
                      <div>
                        {offer.discount_percent > 0 ? (
                          <div className="text-[10px] text-slate-400 line-through font-medium">
                            {formatPrice(pricing.original, currency)}
                          </div>
                        ) : null}
                        <div className="text-xl font-black text-slate-800 tracking-tight">
                          {formatPrice(pricing.discounted, currency)}
                        </div>
                      </div>

                      <span className="text-xs font-bold text-slate-500 group-hover:text-purple-600 transition-colors">
                        View details →
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default OffersPage
